import DashboardSquaresAPI from 'api/Squares/Dashboard/squares.api';
import { updateLoadingState } from 'store/app.slice';
import { setTarget } from 'store/targets.slice';
import { BaseHandler } from '../base.handler';
import { convertDateToString } from './helper';

import TargetAPI from 'api/target.api';
import {
  setDownTimeData,
  setHealthScore,
  setMaintenanceMetrics,
  setSquares,
  setSquaresAssetsDashboard,
  setSquaresDashboard,
} from 'store/SquaresStore/DashboardSquaresState.slice';
import {
  setIsLoading as setIsLoadingPartsMaintenance,
  setPartsMaintenanceExpenditure,
} from 'store/SquaresStore/PartsMaintenanceExpenditureState.slice';
import {
  setIsLoading,
  setPartsRecommendations,
} from 'store/SquaresStore/PartsRecommendationsState.slice';
import { useAppSelector } from 'store/hook';
import { type ICreateSquaresPayload } from 'types/payloads/squares.payload.types';
import { TimedSessionCacheStorage } from 'utils/cache/cache';

export default class SquaresDashboardHandler extends BaseHandler {
  private readonly apiDashboard: DashboardSquaresAPI;
  private readonly apiTarget: TargetAPI;
  private readonly cache: TimedSessionCacheStorage; // timed cache
  private readonly customer = useAppSelector((state) => state.persistedReducer)
    .customer.code;

  constructor() {
    super();
    this.apiDashboard = new DashboardSquaresAPI();
    this.apiTarget = new TargetAPI();
    this.cache = new TimedSessionCacheStorage(`dashboard_${this.customer}`);
  }

  async getPmVsCmVsModification(
    selectedStartDate: Date,
    selectedEndDate: Date
  ) {
    this.dispatch(updateLoadingState(true));
    try {
      const payload: ICreateSquaresPayload = {
        selectedStartDate: convertDateToString(selectedStartDate),
        selectedEndDate: convertDateToString(selectedEndDate),
        customerName: this.customer,
      };
      const squares = await this.apiDashboard.getPmVsCmVsModification(payload);

      this.dispatch(setSquares(squares.Items));
      this.handleSuccess();
      return squares;
    } catch (error: any) {
      let msg: string = '';

      // Extract the error message from AxiosErrors
      // TODO: Look into other errors that can be thrown
      if (error.isAxiosError) {
        msg = error.response.data.message as string;
      }

      this.handleError(
        'An error occured while retrieving the squares : ' + msg
      );
      return [];
    }
  }

  async getPmVsCmExpenditures(selectedStartDate: Date, selectedEndDate: Date) {
    this.dispatch(updateLoadingState(true));
    try {
      const payload: ICreateSquaresPayload = {
        selectedStartDate: convertDateToString(selectedStartDate),
        selectedEndDate: convertDateToString(selectedEndDate),
        customerName: this.customer,
      };
      const squares = await this.apiDashboard.getPmVsCmExpenditures(payload);

      this.dispatch(setSquares(squares.Items));
      this.handleSuccess();
      return squares;
    } catch (error: any) {
      let msg: string = '';

      // Extract the error message from AxiosErrors
      // TODO: Look into other errors that can be thrown
      if (error.isAxiosError) {
        msg = error.response.data.message as string;
      }

      this.handleError(
        'An error occured while retrieving the squares : ' + msg
      );
      return [];
    }
  }

  async getPartsTotalCost(selectedStartDate: Date, selectedEndDate: Date) {
    this.dispatch(updateLoadingState(true));
    try {
      const payload: ICreateSquaresPayload = {
        selectedStartDate: convertDateToString(selectedStartDate),
        selectedEndDate: convertDateToString(selectedEndDate),
        customerName: this.customer,
      };
      const squares = await this.apiDashboard.getPartsTotalCost(payload);

      this.dispatch(setSquares(squares.Items));
      this.handleSuccess();
      return squares;
    } catch (error: any) {
      let msg: string = '';

      // Extract the error message from AxiosErrors
      // TODO: Look into other errors that can be thrown
      if (error.isAxiosError) {
        msg = error.response.data.message as string;
      }

      this.handleError(
        'An error occured while retrieving the squares : ' + msg
      );
      return [];
    }
  }

  async getPartsTotalQuantity(selectedStartDate: Date, selectedEndDate: Date) {
    this.dispatch(updateLoadingState(true));
    try {
      const payload: ICreateSquaresPayload = {
        selectedStartDate: convertDateToString(selectedStartDate),
        selectedEndDate: convertDateToString(selectedEndDate),
        customerName: this.customer,
      };
      const squares = await this.apiDashboard.getPartsTotalQuantity(payload);

      this.dispatch(setSquares(squares.Items));
      this.handleSuccess();
      return squares;
    } catch (error: any) {
      let msg: string = '';

      // Extract the error message from AxiosErrors
      // TODO: Look into other errors that can be thrown
      if (error.isAxiosError) {
        msg = error.response.data.message as string;
      }

      this.handleError(
        'An error occured while retrieving the squares : ' + msg
      );
      return [];
    }
  }

  async getPartsTotalOrderHours(
    selectedStartDate: Date,
    selectedEndDate: Date
  ) {
    this.dispatch(updateLoadingState(true));
    try {
      const payload: ICreateSquaresPayload = {
        selectedStartDate: convertDateToString(selectedStartDate),
        selectedEndDate: convertDateToString(selectedEndDate),
        customerName: this.customer,
      };
      const squares = await this.apiDashboard.getPartsTotalOrderHours(payload);

      this.dispatch(setSquares(squares.Items));
      this.handleSuccess();
      return squares;
    } catch (error: any) {
      let msg: string = '';

      // Extract the error message from AxiosErrors
      // TODO: Look into other errors that can be thrown
      if (error.isAxiosError) {
        msg = error.response.data.message as string;
      }

      this.handleError(
        'An error occured while retrieving the squares : ' + msg
      );
      return [];
    }
  }

  async getDashboardAssetsAscDesc(
    selectedStartDate: Date,
    selectedEndDate: Date,
    sort: string,
    orderBy: string
  ) {
    this.dispatch(updateLoadingState(true));
    try {
      const payload: ICreateSquaresPayload = {
        customerName: this.customer,
        selectedStartDate: convertDateToString(selectedStartDate),
        selectedEndDate: convertDateToString(selectedEndDate),
        sort,
        orderBy,
      };
      const squares = await this.apiDashboard.getDashboardAssetsAscDesc(
        payload
      );

      this.dispatch(setSquares(squares.Items));
      this.handleSuccess();
      return squares;
    } catch (error: any) {
      let msg: string = '';

      // Extract the error message from AxiosErrors
      // TODO: Look into other errors that can be thrown
      if (error.isAxiosError) {
        msg = error.response.data.message as string;
      }

      this.handleError(
        'An error occured while retrieving the squares : ' + msg
      );
      return [];
    }
  }

  async getPartsComponentsData(selectedStartDate: Date, selectedEndDate: Date) {
    this.dispatch(updateLoadingState(true));
    try {
      const payload: ICreateSquaresPayload = {
        selectedStartDate: convertDateToString(selectedStartDate),
        selectedEndDate: convertDateToString(selectedEndDate),
        customerName: this.customer,
      };
      const squares = await this.apiDashboard.getDashboardComponentsData(
        payload
      );

      this.dispatch(setSquaresDashboard(squares));
      this.handleSuccess();
      return squares;
    } catch (error: any) {
      let msg: string = '';

      // Extract the error message from AxiosErrors
      // TODO: Look into other errors that can be thrown
      if (error.isAxiosError) {
        msg = error.response.data.message as string;
      }

      this.handleError(
        'An error occured while retrieving the squares dashboard : ' + msg
      );
      return [];
    }
  }

  async getDashboardAssetsData(selectedStartDate: Date, selectedEndDate: Date) {
    try {
      const payload: ICreateSquaresPayload = {
        selectedStartDate: convertDateToString(selectedStartDate),
        selectedEndDate: convertDateToString(selectedEndDate),
        customerName: this.customer,
      };
      const squares = await this.apiDashboard.getDashboardAssetsData(payload);
      this.dispatch(setSquaresAssetsDashboard(squares));
      // setSquaresAssetsDashboard(squares);
      this.handleSuccess();
      return squares;
    } catch (error: any) {
      let msg: string = '';

      // Extract the error message from AxiosErrors
      // TODO: Look into other errors that can be thrown
      if (error.isAxiosError) {
        msg = error.response.data.message as string;
      }

      this.handleError(
        'An error occured while retrieving the squares dashboard : ' + msg
      );
      return [];
    }
  }

  async getPmVsCmMaintenanceMetrics(
    selectedStartDate: Date,
    selectedEndDate: Date
  ) {
    try {
      this.dispatch(updateLoadingState(true));
      const payload: ICreateSquaresPayload = {
        selectedStartDate: convertDateToString(selectedStartDate),
        selectedEndDate: convertDateToString(selectedEndDate),
        customerName: this.customer,
      };
      const squares = await this.apiDashboard.getPmVsCmMaintenanceMetrics(
        payload
      );
      this.dispatch(setMaintenanceMetrics(squares));
      this.handleSuccess();
      return squares;
    } catch (error: any) {
      let msg: string = '';

      // Extract the error message from AxiosErrors
      // TODO: Look into other errors that can be thrown
      if (error.isAxiosError) {
        msg = error.response.data.message as string;
      }

      this.handleError(
        'An error occured while retrieving the squares asset maintenance data : ' +
          msg
      );
      return [];
    }
  }

  async getHealthScoreData() {
    this.dispatch(updateLoadingState(true));

    const cacheKey = `healthScoreData_${this.customer}`;
    const TTL = 60 * 60 * 1000; // 1 hour

    try {
      // get fresh cached data
      const cachedValue = this.cache.getIfFresh(cacheKey, TTL);

      const isValueValid =
        cachedValue &&
        typeof cachedValue === 'object' &&
        Object.keys(cachedValue).length > 0;

      if (isValueValid) {
        this.dispatch(setHealthScore(cachedValue));
        this.handleSuccess();
        this.dispatch(updateLoadingState(false));
        return cachedValue;
      }

      // Fallback to API call
      const payload: ICreateSquaresPayload = {
        customerName: this.customer,
      };

      const healthScoreData = await this.apiDashboard.getHealthScoreData(
        payload
      );

      // Validate API data before caching
      const isValidAPIResponse =
        healthScoreData &&
        typeof healthScoreData === 'object' &&
        Object.keys(healthScoreData).length > 0;

      if (!isValidAPIResponse) {
        throw new Error('Invalid health score data from API');
      }

      // Store fresh valid data with timestamp
      this.cache.set(cacheKey, healthScoreData);

      this.dispatch(setHealthScore(healthScoreData));
      this.handleSuccess();
      this.dispatch(updateLoadingState(false));
      return healthScoreData;
    } catch (error: any) {
      // Remove bad cache entry (if any)
      this.cache.remove(cacheKey);

      let msg: string = '';
      if (error.isAxiosError) {
        msg = error.response?.data?.message || error.message;
      } else {
        msg = error.message;
      }

      this.handleError(
        'An error occurred while retrieving the health score data: ' + msg
      );
      this.dispatch(updateLoadingState(false));
      return {};
    }
  }

  async getPartsRecommendations(
    selectedStartDate: Date,
    selectedEndDate: Date
  ) {
    this.dispatch(setIsLoading(true));
    try {
      const payload: ICreateSquaresPayload = {
        selectedStartDate: convertDateToString(selectedStartDate),
        selectedEndDate: convertDateToString(selectedEndDate),
        customerName: this.customer,
      };
      const squares = await this.apiDashboard.getPartsRecommendations(payload);
      this.dispatch(setPartsRecommendations(squares));
      this.handleSuccess();
      this.dispatch(setIsLoading(false));
      return squares;
    } catch (error: any) {
      let msg: string = '';

      // Extract the error message from AxiosErrors
      // TODO: Look into other errors that can be thrown
      if (error.isAxiosError) {
        msg = error.response.data.message as string;
      }
      this.dispatch(setIsLoading(false));

      this.handleError(
        'An error occured while retrieving the squares parts Recommendations data : ' +
          msg
      );
      return [];
    }
  }

  async getPartsMaintenanceExpenditure(
    selectedStartDate: Date,
    selectedEndDate: Date,
    updateRedux = true
  ) {
    this.dispatch(setIsLoadingPartsMaintenance(true));
    try {
      const payload: ICreateSquaresPayload = {
        selectedStartDate: convertDateToString(selectedStartDate),
        selectedEndDate: convertDateToString(selectedEndDate),
        customerName: this.customer,
      };
      const squares = await this.apiDashboard.getPartsMaintenanceExpenditure(
        payload
      );
      if (updateRedux) this.dispatch(setPartsMaintenanceExpenditure(squares));
      this.handleSuccess();
      this.dispatch(setIsLoadingPartsMaintenance(false));
      return squares;
    } catch (error: any) {
      let msg: string = '';

      // Extract the error message from AxiosErrors
      // TODO: Look into other errors that can be thrown
      if (error.isAxiosError) {
        msg = error.response.data.message as string;
      }
      this.dispatch(setIsLoadingPartsMaintenance(false));

      this.handleError(
        'An error occured while retrieving the squares parts Maintenance Expenditure data : ' +
          msg
      );
      return [];
    }
  }

  async getTarget(
    customerId: string,
    metricName: string,
    assetType: string,
    year: number,
    signal?: AbortSignal
  ): Promise<any> {
    try {
      const targetPromise = await this.apiTarget.getTargetApi(
        customerId,
        metricName,
        assetType,
        year,
        signal
      );
      targetPromise.targetValue = Number(targetPromise?.targetValue || 0);
      this.dispatch(setTarget(targetPromise));
      return targetPromise;
    } catch (error) {
      console.error(error);
      return {};
    }
  }

  async getWorkOrdersWithDowntimeAggregated(
    selectedStartDate: Date,
    selectedEndDate: Date,
    includePrevious: boolean = false
  ) {
    try {
      const payload = {
        selectedStartDate: convertDateToString(selectedStartDate),
        selectedEndDate: convertDateToString(selectedEndDate),
        customerName: this.customer,
        includePrevious,
        aggregateByAsset: true,
      };

      const response = await this.apiDashboard.getWorkOrdersWithDownTime(
        payload
      );
      this.dispatch(setDownTimeData(response));
      this.handleSuccess();
      return response;
    } catch (error: any) {
      let msg = '';
      if (error.isAxiosError) {
        msg = error.response?.data?.message || error.message;
      }
      this.handleError(
        'An error occurred while fetching work order downtime data: ' + msg
      );
      return {
        current: [],
        previous: [],
      };
    }
  }
}
